/**
 * /kiosk/chip-test — bridge validation page.
 *
 * Loads with no auth, no RPC calls, no business logic. Its only job is to
 * prove the useChipListener hook works end-to-end in the Electron shell.
 *
 *   - Mac/Windows Electron shell with a PC/SC reader (cyberJack, ACR122U,
 *     Identiv uTrust): chip taps come through the IPC bridge
 *   - Plain browser tab: status shows "no-bridge" (no PC/SC channel; this
 *     page is intended for shell debugging, not browser use)
 *
 * Renders the listener status, reader name, last UID in oversized yellow
 * mono text, plus a rolling log of recent taps. Mirrors the spirit of
 * the Electron shell's local test.html.
 */
import { useState } from "react";

import {
  useChipListener,
  type ChipTapEvent,
} from "../../features/klassenbuch-kiosk";

const STATUS_LABELS: Record<string, { text: string; tone: string }> = {
  idle: { text: "initialising", tone: "bg-slate-700 text-slate-200" },
  "electron-ready": { text: "PC/SC bridge bereit", tone: "bg-emerald-900 text-emerald-200" },
  "no-bridge": { text: "kein Electron-Shell", tone: "bg-amber-900 text-amber-200" },
  disabled: { text: "deaktiviert", tone: "bg-amber-900 text-amber-200" },
};

interface LogEntry extends ChipTapEvent {
  seq: number;
}

export default function KioskChipTestPage() {
  const [log, setLog] = useState<LogEntry[]>([]);

  const { status, lastTap, readerName, readerError } = useChipListener({
    onTap: (event) => {
      // Derive the next sequence from the existing log so we don't need a
      // separate `seq` state. The log is the source of truth.
      setLog((prev) => {
        const next = (prev[0]?.seq ?? 0) + 1;
        return [{ ...event, seq: next }, ...prev].slice(0, 20);
      });
    },
  });

  const statusBadge = STATUS_LABELS[status] ?? STATUS_LABELS.idle;
  const formattedUid = lastTap?.uid
    ? lastTap.uid.match(/.{2}/g)?.join(":") ?? lastTap.uid
    : null;

  return (
    <div className="min-h-screen bg-slate-950 text-slate-100 p-8 grid grid-cols-1 lg:grid-cols-2 gap-6">
      <header className="col-span-full flex items-baseline justify-between">
        <h1 className="text-2xl font-semibold text-slate-50">
          Flexwise Kiosk — Chip-Listener-Test
        </h1>
        <span className="text-xs text-slate-500 font-mono">
          /kiosk/chip-test
        </span>
      </header>

      <section className="rounded-xl border border-slate-800 bg-slate-900 p-6 flex flex-col gap-3">
        <h2 className="text-xs font-semibold uppercase tracking-wider text-slate-400">
          Eingangskanal
        </h2>
        <span
          className={`inline-flex w-fit items-center rounded-full px-3 py-1 text-sm font-medium ${statusBadge.tone}`}
        >
          {statusBadge.text}
        </span>
        <div className="font-mono text-sm text-slate-300 break-all">
          {readerName ?? (status === "electron-ready" ? "warte auf Reader…" : "—")}
        </div>
        {readerError ? (
          <div className="font-mono text-xs text-rose-300 break-all">
            ⚠ {readerError}
          </div>
        ) : null}
      </section>

      <section className="rounded-xl border border-slate-800 bg-slate-900 p-6 flex flex-col gap-3">
        <h2 className="text-xs font-semibold uppercase tracking-wider text-slate-400">
          Letzter Chip-Tap
        </h2>
        <div className="font-mono text-3xl lg:text-5xl font-bold text-amber-300 break-all leading-tight">
          {formattedUid ?? "— noch nichts —"}
        </div>
        <div className="text-xs text-slate-500">
          {lastTap
            ? `${log.length} Tap${log.length === 1 ? "" : "s"} • zuletzt um ${new Date(lastTap.ts).toLocaleTimeString()} via ${lastTap.source}`
            : "tippe einen Chip auf das Lesegerät"}
        </div>
      </section>

      <section className="col-span-full rounded-xl border border-slate-800 bg-slate-950/60 p-6 max-h-[40vh] overflow-y-auto">
        <h2 className="text-xs font-semibold uppercase tracking-wider text-slate-400 mb-3">
          Verlauf (letzte 20)
        </h2>
        {log.length === 0 ? (
          <div className="text-sm text-slate-600 italic">noch keine Taps protokolliert</div>
        ) : (
          <ul className="font-mono text-xs space-y-1">
            {log.map((entry) => (
              <li key={entry.seq} className="flex gap-3 items-baseline">
                <span className="text-slate-600 tabular-nums w-8 text-right">
                  #{entry.seq}
                </span>
                <span className="text-slate-500 tabular-nums">
                  {new Date(entry.ts).toLocaleTimeString()}
                </span>
                <span className="text-amber-300">{entry.uid}</span>
                <span className="text-slate-600">via {entry.source}</span>
                {entry.raw && entry.raw !== entry.uid ? (
                  <span className="text-slate-700">raw: {entry.raw}</span>
                ) : null}
              </li>
            ))}
          </ul>
        )}
      </section>

      <footer className="col-span-full text-xs text-slate-600 font-mono">
        Diese Seite ruft keine RPCs auf und schreibt keine Daten — sie validiert
        nur, dass Chip-Taps vom Reader bis in den React-Code durchkommen.
      </footer>
    </div>
  );
}
